import { useEffect, useState } from "react";
import {
  Card,
  Icon,
  MOODS,
  MoodChip,
  Pill,
  cn,
  moodById,
  type MoodId,
} from "@wiseowl/design-system";
import { isApiError, type PatternsSummary, type ReflectionListItem } from "@wiseowl/shared";
import { api } from "../lib/api.js";

type Tab = "patterns" | "history" | "saved";

/**
 * Library — patterns / history / saved tabs.
 *
 * Layout: page header ("A quieter mind, day by day."), pill tab switcher,
 * then a CSS-grid of cards that re-flow on mobile. All copy follows
 * the wise-friend voice; mood-colored bars and chips lean on the
 * mood palette tokens rather than the brand teal/mustard.
 */
export const Library = () => {
  const [tab, setTab] = useState<Tab>("patterns");
  return (
    <div className="space-y-5 pb-6">
      <header>
        <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
          Your library
        </div>
        <h1 className="mt-1.5 font-serif text-display font-medium leading-tight tracking-tight text-ink">
          A quieter mind, <span className="text-mustard">day by day.</span>
        </h1>
      </header>

      <div className="inline-flex gap-1 rounded-pill border border-line bg-paper p-1">
        {(
          [
            ["patterns", "Patterns"],
            ["history", "History"],
            ["saved", "Saved"],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "rounded-pill px-4 py-2 text-small font-semibold transition-colors",
              tab === k ? "bg-ink text-cream" : "text-ink-soft hover:text-ink",
            )}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "patterns" && <PatternsTab />}
      {tab === "history" && <HistoryTab />}
      {tab === "saved" && <SavedTab />}
    </div>
  );
};

const PatternsTab = () => {
  const [patterns, setPatterns] = useState<PatternsSummary | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await api<{ patterns: PatternsSummary }>("/library/patterns");
      if (!isApiError(r)) setPatterns(r.data.patterns);
    })();
  }, []);

  const streakCurrent = patterns?.streak.currentDays ?? 0;
  const totalRefl = patterns?.totalReflections ?? 0;

  // Build a 7-bar mood graph from this week's mood counts. Without
  // per-day buckets server-side yet, we render the count distribution.
  const moodColor: Record<string, string> = {
    calm: "bg-m-calm", stressed: "bg-m-stressed", confused: "bg-m-confused",
    lonely: "bg-m-lonely", tired: "bg-m-tired", happy: "bg-m-happy",
    overthinking: "bg-m-overthinking", unmotivated: "bg-m-unmotivated",
  };
  const maxCount = Math.max(1, ...(patterns?.moodCounts.map((m) => m.count) ?? [1]));
  const days = (patterns?.moodCounts ?? []).slice(0, 7).map((m) => ({
    d: m.moodSlug.slice(0, 1).toUpperCase(),
    h: Math.max(8, Math.round((m.count / maxCount) * 88)),
    cls: moodColor[m.moodSlug] ?? "bg-muted",
  }));
  while (days.length < 7) days.push({ d: "·", h: 8, cls: "bg-line" });

  return (
    <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
      {/* Streak card */}
      <Card
        className="!border-0 bg-gradient-to-br from-mustard to-warning text-ink"
        padding="md"
      >
        <div className="flex items-center gap-1.5 font-mono text-eyebrow font-semibold uppercase tracking-wider">
          <Icon name="flame" size={16} /> Reset streak
        </div>
        <div className="mt-2 font-serif text-[56px] font-medium leading-none tracking-tight">
          {streakCurrent} {streakCurrent === 1 ? "day" : "days"}
        </div>
        <div className="mt-1.5 text-small font-medium">
          {Math.max(0, 21 - streakCurrent)} days until your 21-Day Mind Reset.
        </div>
        <div className="mt-3.5 flex gap-[5px]">
          {Array.from({ length: 21 }).map((_, i) => (
            <div
              key={i}
              className={cn("h-2 flex-1 rounded-sm", i < streakCurrent ? "bg-ink" : "bg-ink/[0.18]")}
            />
          ))}
        </div>
      </Card>

      {/* Mood graph */}
      <Card>
        <div className="flex items-baseline justify-between">
          <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
            Mood this week
          </div>
          <div className="text-meta text-muted">{totalRefl > 0 ? `${totalRefl} reflections so far` : "No reflections yet"}</div>
        </div>
        <div className="mt-4 flex h-[100px] items-end justify-between gap-1.5">
          {days.map((b, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1.5">
              <div
                className={cn("w-full rounded-sm", b.cls)}
                style={{ height: b.h }}
              />
              <div className="text-[11px] font-semibold text-muted">{b.d}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* What's been present — derived from real mood counts. */}
      <Card className="md:col-span-full">
        <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
          What&apos;s been present · recent
        </div>
        {patterns && patterns.moodCounts.length > 0 ? (
          <>
            <div className="mt-3 flex flex-wrap gap-2">
              {patterns.moodCounts.slice(0, 6).map((m) => {
                const mood = moodById(m.moodSlug as MoodId);
                return (
                  <span
                    key={m.moodSlug}
                    className="rounded-pill px-3 py-1 text-meta font-semibold text-ink"
                    style={{ backgroundColor: `${mood?.hex ?? "#9a9a9a"}33` }}
                  >
                    {mood?.name ?? m.moodSlug} · {m.count}
                  </span>
                );
              })}
            </div>
            <div className="mt-4 rounded-md bg-cream-2 p-3.5">
              <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-teal">
                We noticed
              </div>
              <p className="mt-1.5 font-serif text-[15.5px] leading-relaxed text-ink-soft">
                {(() => {
                  const top = patterns.moodCounts[0]!;
                  const name = (moodById(top.moodSlug as MoodId)?.name ?? top.moodSlug).toLowerCase();
                  return `“${name}” has come up ${top.count} ${top.count === 1 ? "time" : "times"} recently. There's no rush to fix it — naming it is already a kind of care.`;
                })()}
              </p>
            </div>
          </>
        ) : (
          <p className="mt-3 font-serif text-[15.5px] italic leading-relaxed text-ink-soft">
            Your patterns will surface here as you reflect over the coming days. A few entries is all it takes.
          </p>
        )}
      </Card>
    </div>
  );
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Today";
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const HistoryTab = () => {
  const [entries, setEntries] = useState<ReflectionListItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      const r = await api<{ reflections: ReflectionListItem[] }>("/reflections?limit=20");
      if (!isApiError(r)) setEntries(r.data.reflections);
      setLoaded(true);
    })();
  }, []);

  if (loaded && entries.length === 0) {
    return (
      <Card className="text-center" padding="lg">
        <p className="font-serif text-h3 text-ink">A quieter mind, day by day.</p>
        <p className="mt-2 text-small text-muted">Your reflections will show here once you start writing.</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {entries.map((e) => {
        const m = e.moodSlug ? moodById(e.moodSlug as MoodId) : undefined;
        return (
          <Card key={e.id} className="cursor-pointer">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {m && <MoodChip mood={m} />}
                <span className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
                  {formatWhen(e.createdAt)}{e.moodSlug ? ` · ${e.moodSlug}` : ""}
                </span>
              </div>
              <Icon name="bookmark" size={16} className={e.saved ? "text-mustard" : "text-muted-2"} />
            </div>
            <p className="mt-2.5 font-serif text-body italic leading-snug text-ink-soft">
              &ldquo;{e.preview}{e.preview.length >= 140 ? "…" : ""}&rdquo;
            </p>
          </Card>
        );
      })}
    </div>
  );
};

const SavedTab = () => {
  const [saved, setSaved] = useState<ReflectionListItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void (async () => {
      const r = await api<{ reflections: ReflectionListItem[] }>("/library/saved");
      if (!isApiError(r)) setSaved(r.data.reflections);
      setLoaded(true);
    })();
  }, []);

  if (loaded && saved.length === 0) {
    return (
      <Card className="text-center" padding="lg">
        <p className="font-serif text-h3 text-ink">Nothing saved yet.</p>
        <p className="mt-2 text-small text-muted">Tap the bookmark on any reflection to keep it here.</p>
      </Card>
    );
  }

  return (
    <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
      {saved.map((s) => (
        <Card key={s.id} surface="cream-2" className="relative">
          <span
            aria-hidden="true"
            className="absolute right-3.5 top-2 select-none font-serif text-[60px] leading-[0.8] text-mustard/40"
          >
            &ldquo;
          </span>
          <p className="pr-7 font-serif text-h3 italic leading-snug text-ink">{s.preview}</p>
          <div className="mt-3.5 text-meta text-muted">Saved from your reflections</div>
        </Card>
      ))}
    </div>
  );
};
